import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';


import { MenuItem, MenuDivider, Menu, MenuButton } from '@szhsin/react-menu';
import classNames from 'classnames';

import { useAuthCtx } from '@anyx/common-auth';
import { UserRoutePath } from '@anyx/common-routing';
import { useSelfCtx } from '@anyx/common-self';
import { useFeatureFlag } from '@anyx/external-firebase';
import { UserSettingsIcon, SignOutIcon, NotificationsIcon } from '@anyx/shared-icons';

import { useUnreadCount } from './hooks';
import { UnreadNotificationsPill } from './notifications';

export const UserMenu = () => {
  const {
    self: { role, name, email, picture },
  } = useSelfCtx();
  const { logout } = useAuthCtx();
  const navigate = useNavigate();
  const { t } = useTranslation(['shared', 'gql', 'core']);
  const isMkpNotificationActiveFlag = useFeatureFlag('mkp_notification_active')?.value;
  const { unread: count, loading } = useUnreadCount({ skip: !isMkpNotificationActiveFlag });

  return (
    <Menu
      menuButton={({ open }) => (
        <MenuButton
          className={classNames(
            'hover:bg-gray-0 group flex w-full cursor-pointer items-center justify-between px-4 py-3 text-xs font-semibold lg:p-4',
            {
              'bg-gray-0': open,
            }
          )}
        >
          <div className="flex items-center">
            <img loading="lazy" alt={name} className="h-6 w-6 rounded-full" src={picture} />
            <span
              title={name}
              className="ml-2 hidden w-3/4 truncate break-all first-letter:uppercase lg:block"
            >
              {name}
            </span>
          </div>
          {isMkpNotificationActiveFlag && count > 0 && !loading && (
            <UnreadNotificationsPill count={count} />
          )}
        </MenuButton>
      )}
      direction="top"
      viewScroll="auto"
      align="start"
      shift={0}
      portal={true}
      menuClassName="border-l-0 border-r-0 border-b-0 shadow-user rounded-none w-56"
    >
      <div className="p-4">
        <p className="text-gray-7 text-3xs font-normal">
          {t('gql.enum.userRole', { role: role, ns: 'gql' })}
        </p>
        <p className="text-2xs mt-2 font-semibold">{name}</p>
        <p className="text-main text-3xs mt-1 block overflow-hidden font-normal text-ellipsis whitespace-nowrap">
          {email}
        </p>
      </div>
      <MenuDivider className="m-0" />
      <MenuItem
        onClick={() => navigate(UserRoutePath().self().path)}
        className={
          'text-main hover:bg-gray-0 m-2 flex items-center rounded-sm px-2 py-1 font-semibold'
        }
      >
        <UserSettingsIcon className="fill-gray-7 h-5.5 w-5.5" />
        <span className="ml-2 text-xs">
          {t('shared.action.settings', { entity: t('shared.entity.user', { count: 1 }) })}
        </span>
      </MenuItem>
      {isMkpNotificationActiveFlag && (
        <MenuItem
          onClick={() => navigate(UserRoutePath().notifications().path)}
          className={
            'text-main hover:bg-gray-0 m-2 flex items-center justify-between rounded-sm px-2 py-1 font-semibold'
          }
        >
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center">
              <NotificationsIcon className="fill-red-6 h-5.5 w-5.5" />
              <span className="ml-2 text-xs">
                {t('core.page.notifications.title', { ns: 'core' })}
              </span>
            </div>
            {count > 0 && !loading && <UnreadNotificationsPill count={count} />}
          </div>
        </MenuItem>
      )}
      <MenuItem
        onClick={() => logout()}
        className={
          'text-main hover:bg-gray-0 m-2 flex items-center rounded-sm px-2 py-1 font-semibold'
        }
      >
        <SignOutIcon className="fill-gray-7 h-5.5 w-5.5" />
        <span className="ml-2 text-xs">{t('shared.button.signOut')}</span>
      </MenuItem>
      <MenuDivider className="m-0" />
    </Menu>
  );
};
